// src/data/m2/Unit6.js

export const M2_UNIT_6 = {
  id: "m2-u6",
  title: "Unit 6: Articles & Specifics",
  topic: "A/An, The, Some & Any",
  iconType: "star", // O 'bookmark' para denotar especificidad
  grammarImage: "/m2-u6-grammar.jpg", 
  grammarText: "Regla de Oro: **'A/An'** es para uno cualquiera. **'The'** es para uno específico (que ambos conocemos). **'Some'** para plurales positivos.",
  
  sets: [
    // ----------------------------------------------------------------------
    // SET 1: A vs AN (The Sound Rule)
    // Enfoque: Sonido, no solo letra. (University vs Umbrella).
    // ----------------------------------------------------------------------
    {
      id: "u22-set1",
      title: "Set 1: A or An?",
      questions: [
        { 
          type: "mc", 
          q: "She is ___ artist.", 
          options: ["an", "a"], 
          a: "an",
          tip: "Artist empieza con sonido vocal -> An."
        },
        { 
          type: "mc", 
          q: "I am ___ doctor.", 
          options: ["a", "an"], 
          a: "a" 
        },
        { 
          type: "mc", 
          q: "It takes ___ hour to get there.", 
          options: ["an", "a"], 
          a: "an",
          successTip: "¡Ojo! La 'H' en 'Hour' es muda. Suena como 'Our' -> An hour."
        },
        { 
          type: "mc", 
          q: "He goes to ___ university.", 
          options: ["a", "an"], 
          a: "a",
          tip: "University empieza con sonido 'Yu' (consonante) -> A university."
        },
        { 
          type: "input", 
          q: "Escribe: ___ egg (huevo)", 
          a: "an" 
        }
      ]
    },

    // ----------------------------------------------------------------------
    // SET 2: GENERAL VS SPECIFIC (A vs THE)
    // Enfoque: Contexto de conversación.
    // ----------------------------------------------------------------------
    {
      id: "u22-set2",
      title: "Set 2: General vs Specific",
      questions: [
        { 
          type: "match", 
          q: "Conecta el significado:",
          pairs: [
            { left: "I want a apple", right: "Any apple (Cualquiera)" },
            { left: "I want the apple", right: "That specific one (Esa)" }
          ],
          a: "completed"
        },
        { 
          type: "mc", 
          q: "Can I have ___ menu, please?", 
          options: ["the", "a"], 
          a: "the",
          tip: "En un restaurante, pides 'el' menú del lugar (específico) -> The."
        },
        { 
          type: "blank", 
          q: "Look at ___ moon!", 
          options: ["the"], 
          a: "the",
          successTip: "Solo hay una luna en nuestro cielo, así que siempre es 'The moon'."
        },
        { 
          type: "mc", 
          q: "I need to buy ___ new car.", 
          options: ["a", "the"], 
          a: "a",
          tip: "Aún no sabemos cuál coche, es uno general -> A."
        },
        { 
          type: "order", 
          q: "Ordena: Cierra la puerta, por favor.", 
          words: ["Close", "the", "door,", "please."], 
          a: "Close the door, please." // Específica (la de esta habitación)
        }
      ]
    },

    // ----------------------------------------------------------------------
    // SET 3: SOME VS ANY (Basic Rules)
    // Enfoque: Plurales.
    // ----------------------------------------------------------------------
    {
      id: "u22-set3",
      title: "Set 3: Plurals (Some/Any)",
      questions: [
        { 
          type: "mc", 
          q: "There are ___ hotels in the city centre.", 
          options: ["some", "any"], 
          a: "some" 
        },
        { 
          type: "mc", 
          q: "There aren't ___ cheap hotels here.", 
          options: ["any", "some"], 
          a: "any",
          tip: "Negativo -> Any."
        },
        { 
          type: "blank", 
          q: "Are there ___ messages for me?", 
          options: ["any"], 
          a: "any",
          tip: "Pregunta estándar -> Any."
        },
        { 
          type: "input", 
          q: "Escribe: Tengo algunos amigos.", 
          a: "I have some friends" // Flexible si el motor soporta 'some friends'
        },
        { 
          type: "match", 
          q: "Reglas de Plural:",
          pairs: [
            { left: "Positive (+)", right: "Some" },
            { left: "Negative (-)", right: "Any" },
            { left: "Question (?)", right: "Any" }
          ],
          a: "completed"
        }
      ]
    },

    // ----------------------------------------------------------------------
    // SET 4: CONVERSATIONAL EXCEPTION (Offers & Requests)
    // Enfoque: Pro Tip - ¿Cuándo usamos Some en preguntas?
    // ----------------------------------------------------------------------
    {
      id: "u22-set4",
      title: "Set 4: Polite Offers (Pro Tip)",
      questions: [
        { 
          type: "mc", 
          q: "Would you like ___ coffee? (Ofrecimiento)", 
          options: ["some", "any"], 
          a: "some",
          successTip: "¡Pro Tip! Cuando ofrecemos algo y esperamos un 'Sí', usamos **Some**."
        },
        { 
          type: "mc", 
          q: "Can I have ___ water, please? (Petición)", 
          options: ["some", "any"], 
          a: "some",
          tip: "Cuando pides algo (Request), usas **Some**."
        },
        { 
          type: "order", 
          q: "Pide educadamente: ¿Puedo tomar agua?", 
          words: ["Can", "I", "have", "some", "water?"], 
          a: "Can I have some water?" 
        },
        { 
          type: "mc", 
          q: "Do you have ___ brothers? (Pregunta normal de info)", 
          options: ["any", "some"], 
          a: "any",
          tip: "Aquí solo pides información, no ofreces hermanos -> Any."
        }
      ]
    },

    // ----------------------------------------------------------------------
    // SET 5: AT A RESTAURANT (Real Life)
    // Enfoque: Aplicar A/The/Some en contexto.
    // ----------------------------------------------------------------------
    {
      id: "u22-set5",
      title: "Set 5: At a Restaurant",
      questions: [
        { 
          type: "mc", 
          q: "Waiter: Do you have ___ reservation?", 
          options: ["a", "the"], 
          a: "a" 
        },
        { 
          type: "blank", 
          q: "Customer: Yes, I want ___ table by the window.", 
          options: ["the"], 
          a: "the",
          tip: "Quieres ESA mesa específica."
        },
        { 
          type: "order", 
          q: "Pide la cuenta: Can I have the check?", 
          words: ["Can", "I", "have", "the", "check,", "please?"], 
          a: "Can I have the check, please?",
          successTip: "En UK dirían 'The bill', en US 'The check'."
        },
        { 
          type: "mc", 
          q: "Customer: I'd like ___ pasta.", 
          options: ["some", "a"], 
          a: "some" 
        }
      ]
    },

    // ----------------------------------------------------------------------
    // SET 6: LOST & FOUND (Specific Objects)
    // Enfoque: 'The' vs Possessives.
    // ----------------------------------------------------------------------
    {
      id: "u22-set6",
      title: "Set 6: Lost & Found",
      questions: [
        { 
          type: "mc", 
          q: "Where are ___ keys? (Specific ones)", 
          options: ["the", "a"], 
          a: "the" 
        },
        { 
          type: "blank", 
          q: "Is this ___ (tu) bag?", 
          options: ["your"], 
          a: "your" 
        },
        { 
          type: "mc", 
          q: "I can't find ___ hotel.", 
          options: ["the", "a"], 
          a: "the",
          tip: "Te refieres al hotel donde te alojas (específico)."
        },
        { 
          type: "order", 
          q: "Pregunta: ¿Dónde está el coche?", 
          words: ["Where", "is", "the", "car?"], 
          a: "Where is the car?" 
        }
      ]
    },

    // ----------------------------------------------------------------------
    // SET 7: ERROR CORRECTION (Grammar Police)
    // ----------------------------------------------------------------------
    {
      id: "u22-set7",
      title: "Set 7: Find the Mistake",
      questions: [
        { 
          type: "mc", 
          q: "Error: 'Can I have any water?' (Request)", 
          options: ["Fix: SOME water", "Fix: A water"], 
          a: "Fix: SOME water",
          tip: "En peticiones (Can I have...) usamos Some."
        },
        { 
          type: "mc", 
          q: "Error: 'She is a engineer.'", 
          options: ["Fix: AN engineer", "Fix: THE engineer"], 
          a: "Fix: AN engineer",
          tip: "Engineer empieza por vocal."
        },
        { 
          type: "input", 
          q: "Corrige: 'Open a door.' (Hay solo una puerta, es específica)", 
          a: "Open the door"
        },
        { 
          type: "mc", 
          q: "Error: 'I don't have some money.'", 
          options: ["Fix: ANY money", "Fix: A money"], 
          a: "Fix: ANY money",
          tip: "Negativo -> Any."
        }
      ]
    },

    // ----------------------------------------------------------------------
    // SET 8: MASTER CHALLENGE (Story Filling)
    // Enfoque: Completar una historia corta.
    // ----------------------------------------------------------------------
    {
      id: "u22-set8",
      title: "Set 8: Story Challenge",
      questions: [
        { 
          type: "mc", 
          q: "STORY: I arrived at ___ airport.", 
          options: ["the", "a"], 
          a: "the",
          tip: "El aeropuerto de esa ciudad específica."
        },
        { 
          type: "blank", 
          q: "I took ___ (un) taxi to the hotel.", 
          options: ["a"], 
          a: "a" 
        },
        { 
          type: "mc", 
          q: "___ driver was very friendly.", 
          options: ["The", "A"], 
          a: "The",
          successTip: "Usamos 'The' porque ya mencionamos el taxi, así que sabemos qué conductor es."
        },
        { 
          type: "order", 
          q: "Ordena: Él me dio algunos consejos.", 
          words: ["He", "gave", "me", "some", "tips."], 
          a: "He gave me some tips." 
        },
        { 
          type: "input", 
          q: "Final: 'It was ___ nice trip.' (a/an)", 
          a: "a" 
        }
      ]
    }
  ]
};